/**
 * Station renderer: a seeded orbital hub — core spindle, outer ring on
 * spokes, four lit landing pads on the ring's top face. The pose comes
 * from the shared station math every frame (a continuous function of the
 * render clock — nothing to tick or interpolate), so the mesh, the pads,
 * the docked ships and every other client's view of them all agree.
 */
import * as THREE from "three";
import {
  stationFor,
  stationPose,
  padWorld,
  rngFromString,
  type StationDef,
  type SystemDef,
} from "@hyperlight/shared";

export class StationVis {
  readonly def: StationDef | null;
  readonly group = new THREE.Group();
  private padLights: THREE.Sprite[] = [];
  private axisY = new THREE.Vector3(0, 1, 0);

  constructor(
    private scene: THREE.Scene,
    systemDef: SystemDef,
  ) {
    this.def = stationFor(systemDef);
    if (!this.def) return;
    const st = this.def;
    const rng = rngFromString(`${st.seed}:vis`);
    const R = st.radius;

    const hull = new THREE.MeshLambertMaterial({ color: 0x9aa6b4 });
    const dark = new THREE.MeshLambertMaterial({ color: 0x57606c });
    const glowMat = new THREE.MeshLambertMaterial({
      color: 0x223344,
      emissive: new THREE.Color(0x66ccff),
      emissiveIntensity: 0.9,
    });

    // Core spindle.
    const core = new THREE.Mesh(new THREE.CylinderGeometry(R * 0.22, R * 0.26, R * 0.9, 10), hull);
    this.group.add(core);
    const cap = new THREE.Mesh(new THREE.CylinderGeometry(R * 0.1, R * 0.22, R * 0.25, 10), dark);
    cap.position.y = R * 0.55;
    this.group.add(cap);

    // Outer ring (flat in the orbit plane).
    const ring = new THREE.Mesh(new THREE.TorusGeometry(R * 0.82, R * 0.09, 8, 36), hull);
    ring.rotation.x = Math.PI / 2;
    this.group.add(ring);

    // Spokes + window strips.
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * Math.PI * 2 + Math.PI / 4;
      const spoke = new THREE.Mesh(new THREE.BoxGeometry(R * 0.74, R * 0.06, R * 0.08), dark);
      spoke.position.set(Math.cos(a) * R * 0.43, 0, Math.sin(a) * R * 0.43);
      spoke.rotation.y = -a;
      this.group.add(spoke);
      const strip = new THREE.Mesh(new THREE.BoxGeometry(R * 0.5, R * 0.02, R * 0.02), glowMat);
      strip.position.set(Math.cos(a) * R * 0.45, R * 0.05, Math.sin(a) * R * 0.45);
      strip.rotation.y = -a;
      this.group.add(strip);
    }

    // Seeded greebles on the core.
    for (let i = 0; i < 8; i++) {
      const g = new THREE.Mesh(
        new THREE.BoxGeometry(R * (0.04 + rng() * 0.07), R * (0.05 + rng() * 0.12), R * 0.05),
        rng() < 0.5 ? dark : hull,
      );
      const a = rng() * Math.PI * 2;
      g.position.set(Math.cos(a) * R * 0.25, (rng() - 0.4) * R * 0.6, Math.sin(a) * R * 0.25);
      g.rotation.y = -a;
      this.group.add(g);
    }

    // Landing pads on the ring top + beacon sprites.
    const padMat = new THREE.MeshLambertMaterial({ color: 0x3c4754 });
    const beaconTex = makeGlowTex();
    for (let i = 0; i < st.pads.length; i++) {
      const p = st.pads[i];
      const puck = new THREE.Mesh(new THREE.CylinderGeometry(R * 0.14, R * 0.16, R * 0.04, 12), padMat);
      puck.position.set(p.local[0], p.local[1] - R * 0.03, p.local[2]);
      this.group.add(puck);
      const lip = new THREE.Mesh(new THREE.TorusGeometry(R * 0.14, R * 0.012, 6, 18), glowMat);
      lip.rotation.x = Math.PI / 2;
      lip.position.set(p.local[0], p.local[1] - R * 0.01, p.local[2]);
      this.group.add(lip);
      const beacon = new THREE.Sprite(
        new THREE.SpriteMaterial({
          map: beaconTex,
          color: 0x77d8ff,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
          transparent: true,
          opacity: 0.5,
        }),
      );
      beacon.scale.set(R * 0.3, R * 0.3, 1);
      beacon.position.set(p.local[0], p.local[1] + R * 0.06, p.local[2]);
      this.group.add(beacon);
      this.padLights.push(beacon);
    }

    this.group.visible = true;
    scene.add(this.group);
  }

  /** Per render frame: continuous pose from the shared clock. */
  update(tRender: number, origin: THREE.Vector3): void {
    if (!this.def) return;
    const pose = stationPose(this.def, tRender);
    this.group.position.set(
      pose.center[0] - origin.x,
      pose.center[1] - origin.y,
      pose.center[2] - origin.z,
    );
    this.group.quaternion.setFromAxisAngle(this.axisY, pose.spinAngle);
    // Beacons breathe gently, offset per pad.
    for (let i = 0; i < this.padLights.length; i++) {
      (this.padLights[i].material as THREE.SpriteMaterial).opacity =
        0.35 + 0.25 * (0.5 + 0.5 * Math.sin(tRender * 1.8 + i * 1.7));
    }
  }

  /** World-space pad transform at tRender (live, shared math). */
  pad(i: number, tRender: number): { pos: THREE.Vector3; up: THREE.Vector3; nose: THREE.Vector3 } | null {
    if (!this.def) return null;
    const w = padWorld(this.def, stationPose(this.def, tRender), i);
    return {
      pos: new THREE.Vector3(...w.pos),
      up: new THREE.Vector3(...w.up),
      nose: new THREE.Vector3(...w.nose),
    };
  }

  /** Nearest pad to `pos` at tRender, with distance. */
  nearestPad(pos: THREE.Vector3, tRender: number): { i: number; dist: number } | null {
    if (!this.def) return null;
    let best = -1;
    let bestD = Infinity;
    for (let i = 0; i < this.def.pads.length; i++) {
      const w = padWorld(this.def, stationPose(this.def, tRender), i);
      const d = Math.hypot(pos.x - w.pos[0], pos.y - w.pos[1], pos.z - w.pos[2]);
      if (d < bestD) {
        bestD = d;
        best = i;
      }
    }
    return best >= 0 ? { i: best, dist: bestD } : null;
  }

  setVisible(v: boolean): void {
    this.group.visible = v && this.def !== null;
  }

  dispose(): void {
    this.scene.remove(this.group);
    this.group.traverse((o) => {
      if (o instanceof THREE.Mesh) {
        o.geometry.dispose();
        (o.material as THREE.Material).dispose();
      }
      if (o instanceof THREE.Sprite) o.material.dispose();
    });
  }
}

function makeGlowTex(): THREE.CanvasTexture {
  const size = 64;
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext("2d")!;
  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  g.addColorStop(0, "rgba(255,255,255,0.95)");
  g.addColorStop(0.35, "rgba(255,255,255,0.3)");
  g.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  return new THREE.CanvasTexture(canvas);
}
